// src/features/records/components/RecordDetail/components/HeroSection.js
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import RecordDropdownMenu from '../../shared/RecordDropdownMenu';
import RecordAccessIndicator from './RecordAccessIndicator';
import { Avatar, AvatarSkeleton } from '../../../../../components/avatars';
import { formatTimeAgo } from '../../../../../utils/dateUtils';
import { sanitizeText } from '../../../../../utils/sanitize';
import useUserProfile from '../../../../../hooks/useUserProfile';
import { DISPLAY_MODE } from '../../../constants';
import styles from '../RecordDetail.module.css';

const HeroSection = ({ 
  record, 
  mode,
  onEdit, 
  onDelete,
  onShare,
  canEdit,
  titleId 
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language;
  const unknownUserLabel = t('records.detail.author.unknown', 'Unknown user');
  const fallbackAvatarAlt = t('records.detail.author.avatar_fallback_alt', 'User avatar');
  const [showDropdown, setShowDropdown] = React.useState(false);
  
  // Fetch author profile
  const { 
    profile: authorProfile, 
    loadingProfile: profileLoading 
  } = useUserProfile(record?.access?.owner);

  const handleEdit = () => {
    if (onEdit) {
      onEdit(record);
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(record);
    }
  };

  const handleShare = () => {
    if (onShare) {
      onShare(record);
    }
  };

  // Convert Firestore Timestamp to Date object if needed
  const getCreatedDate = () => {
    const timestamp = record?.metadata?.createdAt || record?.createdAt;
    if (!timestamp) return new Date();
    
    // Handle Firestore Timestamp object (same pattern as Post.js)
    if (timestamp instanceof Object && 'toDate' in timestamp) {
      return timestamp.toDate();
    }
    
    // Handle string or Date object
    return new Date(timestamp);
  };
  
  const createdAt = getCreatedDate();

  return (
    <div className={styles.heroSection} data-testid="hero-section">
      {/* Author Section */}
      {profileLoading && !authorProfile ? (
        <div className={styles.authorSection}>
          <div className={styles.authorLeft}>
            <AvatarSkeleton
              size="lg"
              className={styles.avatarSkeleton}
              data-testid="avatar-skeleton"
            />
            <div className={styles.authorInfoSkeleton}>
              <div className={styles.nameSkeleton} />
              <div className={styles.metaSkeleton} />
            </div>
          </div>
          <div className={styles.authorRight} />
        </div>
      ) : (
        <div className={styles.authorSection}>
          <div className={styles.authorLeft}>
            <Avatar
              name={authorProfile?.displayName || unknownUserLabel}
              src={authorProfile?.profilePictureUrl}
              size="lg"
              alt={
                authorProfile?.displayName
                  ? t('records.detail.author.avatar_alt', {
                    name: authorProfile.displayName,
                    defaultValue: "{{name}}'s avatar"
                  })
                  : fallbackAvatarAlt
              }
            />
            <div className={styles.authorInfo}>
              <div className={styles.authorName}>
                {sanitizeText(authorProfile?.displayName) || unknownUserLabel}
              </div>
              <div className={styles.authorMeta}>
                <time dateTime={createdAt instanceof Date && !isNaN(createdAt) ? createdAt.toISOString() : ''}>
                  {createdAt instanceof Date && !isNaN(createdAt)
                    ? formatTimeAgo(createdAt, { locale, t })
                    : t('common.invalid_date', 'Invalid date')}
                </time>
              </div>
            </div>
          </div>

          <div className={styles.authorRight}>
            {/* Access Indicator - Right aligned */}
            <RecordAccessIndicator recordId={record.id} access={record.access} />

            {/* Edit Actions (only in page mode and if user can edit) */}
            {canEdit && mode === DISPLAY_MODE.PAGE && (
              <div className={styles.heroActions}>
                <RecordDropdownMenu
                  isOpen={showDropdown}
                  onToggle={setShowDropdown}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onShare={handleShare}
                  record={record}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Title */}
      {record.content?.title && (
        <h1 id={titleId} className={styles.recordTitle}>
          {sanitizeText(record.content.title)}
        </h1>
      )}

      {/* Tags */}
      {record.content?.tags?.length > 0 && (
        <div className={styles.tagList}>
          {record.content.tags.map((tag, index) => (
            <span key={index} className={styles.tag}>
              {sanitizeText(tag)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

HeroSection.propTypes = {
  record: PropTypes.shape({
    id: PropTypes.string.isRequired,
    content: PropTypes.shape({
      title: PropTypes.string,
      tags: PropTypes.arrayOf(PropTypes.string)
    }),
    access: PropTypes.shape({
      owner: PropTypes.string,
      visibility: PropTypes.oneOf(['private', 'friends', 'public'])
    }),
    metadata: PropTypes.shape({
      createdAt: PropTypes.any
    })
  }).isRequired,
  mode: PropTypes.oneOf(['modal', 'page']),
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onShare: PropTypes.func,
  canEdit: PropTypes.bool,
  titleId: PropTypes.string
};

export default HeroSection;
